import React, { useContext, useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MarktingContext } from "../../Context/MarktingContext";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { SetupContext } from "../../Context/SetupContext";
import SelectOption from "./SelectOption";
import css from "./style.module.css";

export default function AddMarktingForm({ animationPlayed, setAnimationPlayed, setAppear, marketingDocId = null, setDocInfo, loginToken, setDocumentStatus }) {
    const version = `version/${marketingDocId}`;
    const { setMarktingData, marktingData } = useContext(MarktingContext);
    const { manageFields } = useContext(SetupContext);

    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStage, setUploadStage] = useState(""); // New state for upload stages
    const setFieldValueRef = useRef();
    const formikRef = useRef();
    const [error, setError] = useState("");
    const navigation = useNavigate();
    const MAX_FILE_SIZE = 105 * 1024 * 1024; // 105MB in bytes

    useEffect(() => {
        if (animationPlayed) {
            setFiles([]);
            setUploadProgress(0);
            setUploadStage("");
            setError("");
        }
    }, [animationPlayed]);

    const closeModal = () => {
        if (loading) return; // don't dismiss while upload in flight
        if (typeof setAnimationPlayed === "function") setAnimationPlayed(false);
    };

    useEffect(() => {
        if (!animationPlayed) return;
        const onKey = (e) => { if (e.key === "Escape") closeModal(); };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [animationPlayed, loading]);

    const validationSchema = Yup.object().shape({
        ...(!marketingDocId && {
            documentName: Yup.string().required("Document name is required."),
            fields: Yup.object().shape({
                documentType: Yup.array()
                    .min(1, "Select at least one document type.")
                    .required("Document type is required."),
                mediaChannel: Yup.array()
                    .min(1, "Select at least one media channel.")
                    .required("Media channel is required."),
                targetGeography: Yup.array()
                    .min(1, "Select at least one geography.")
                    .required("Target geography is required."),
                prioritiesLevel: Yup.string()
                    .required("Priority level is required."),
            }),
        }),
        file: Yup.mixed()
            .required("A file is required.")
            .test(
                "fileType",
                "Unsupported file format. Please upload an HTML file, an Image or a PDF.",
                (value) =>
                    value &&
                    [
                        "image/jpeg",
                        "image/webp",
                        "image/png",
                        "image/gif",
                        "application/pdf",
                        "text/html"
                    ].includes(value.type)
            )
            .test(
                "fileSize",
                "File size is too large. Maximum file size is 105MB.",
                (value) => value && value.size <= MAX_FILE_SIZE
            ),
        description: Yup.string(),
    });

    // Check if all required fields are completed
    const checkFormCompletion = (values) => {
        if (marketingDocId) {
            return true; // For version uploads, only file is required
        }

        return (
            values.documentName &&
            values.fields.documentType.length > 0 &&
            values.fields.mediaChannel.length > 0 &&
            values.fields.targetGeography.length > 0 &&
            values.fields.prioritiesLevel
        );
    };

    const uploadFiles = async (values, resetForm, selectedFile = null) => {
        setLoading(true);
        setUploadProgress(0);
        setUploadStage("Preparing upload...");
        
        // Use the passed file or get from files array or values
        const file = selectedFile || files[0] || values.file;
        
        if (!file) {
            setError("No file selected for upload.");
            setLoading(false);
            return;
        }

        const foundName = marktingData.find(item => item.documentName === values.documentName);

        if (foundName && !marketingDocId) {
            setError("Document name already exists.");
            setLoading(false);
            setUploadStage("");
            return;
        }
        
        try {
            if (file.size > MAX_FILE_SIZE) {
                setError("File size is too large. Please upload a smaller file. Max file size: 105MB");
                setLoading(false);
                setUploadStage("");
                return;
            }

            // Step 1: Get signed URL from your server
            setUploadStage("Getting upload permission...");
            setUploadProgress(10);
            
            const signedUrlResponse = await axios.post(
                `${process.env.REACT_APP_API_URL}/signed-url`,
                {
                    fileName: file.name,
                    contentType: file.type,
                },
                {
                    headers: {
                        Authorization: `Bearer ${loginToken}`,
                    },
                }
            );

            const { signedUrl, publicUrl, fileName } = signedUrlResponse.data;

            // Step 2: Upload file directly to GCS with progress tracking
            setUploadStage("Uploading file...");
            setUploadProgress(15);
            
            const xhr = new XMLHttpRequest();
            
            const uploadPromise = new Promise((resolve, reject) => {
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const fileUploadProgress = Math.round((event.loaded / event.total) * 70); // 70% of total progress
                        setUploadProgress(15 + fileUploadProgress); // Start from 15%
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        setUploadProgress(85);
                        setUploadStage("File uploaded successfully...");
                        resolve(xhr.response);
                    } else {
                        reject(new Error(`Upload failed with status: ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Upload failed'));
                });

                xhr.open('PUT', signedUrl);
                xhr.setRequestHeader('Content-Type', file.type);
                xhr.send(file);
            });

            await uploadPromise;

            // Step 3: Send metadata to your server
            setUploadStage("Processing document...");
            setUploadProgress(90);
            
            const payload = {
                documentName: values.documentName,
                documentType: marketingDocId ? undefined : values.fields.documentType,
                mediaChannel: marketingDocId ? undefined : values.fields.mediaChannel,
                targetGeography: marketingDocId ? undefined : values.fields.targetGeography,
                prioritiesLevel: marketingDocId ? undefined : values.fields.prioritiesLevel,
                description: values.description.trim(),
                fileName: fileName,
                mimeType: file.type,
                fileUrl: publicUrl,
                fileSize: file.size,
            };

            const { data } = await axios.post(
                `${process.env.REACT_APP_API_URL}/${marketingDocId ? version : 'marketing'}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${loginToken}`,
                    },
                }
            );

            // Step 4: Update state with response
            setUploadProgress(95);
            setUploadStage("Finalizing...");
            
            const res = data?.data;
            if (res?.marketingDocumentId) {
                const deepClone = JSON.parse(JSON.stringify(marktingData));
                const index = deepClone.findIndex((item) => item.documentId === res.marketingDocumentId);
                if (index !== -1) {
                    deepClone[index].version = deepClone[index].version === undefined ? [res] : deepClone[index].version.concat(res);
                    deepClone[index].version = deepClone[index].version.sort((a, b) => b.versionNumber - a.versionNumber);

                    setDocInfo(deepClone[index]);
                    setDocumentStatus(res.Document.status);
                    setMarktingData(deepClone);
                }
            } else {
                setMarktingData((prevData) => [...prevData, data.data]);
            }

            setLoading(false);
            setUploadProgress(100);
            setUploadStage("Upload completed successfully!");
            
            // Reset after successful upload
            setTimeout(() => {
                setUploadProgress(0);
                setUploadStage("");
                setFiles([]);
                resetForm();
                
                if (typeof setAnimationPlayed === 'function') {
                    setAnimationPlayed(!animationPlayed);
                    setAppear(true);
                }
            }, 2000); // Increased delay to show completion message

            setError("");

        } catch (error) {
            console.error("Upload error:", error);

            if (error.response && error?.response?.status === 401) {
                localStorage.removeItem("Firebase Auth");
                localStorage.removeItem("okta-token-storage");
                localStorage.removeItem("okta-cache-storage");
                navigation('/');
                return;
            }

            if (error?.response?.data?.error) {
                setError(error.response.data.error);
            } else if (error.message && error.message.includes('GCS upload failed')) {
                setError("Failed to upload file to cloud storage. Please try again.");
            } else {
                setError("An error occurred during upload");
            }
            setLoading(false);
            setUploadProgress(0);
            setUploadStage("");
        }
    };

    // Auto-upload when file is selected and form is complete
    const handleFileSelection = async (file, formikProps) => {
        if (!file) {
            setError("No file selected.");
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setError("File size is too large. Please upload a smaller file. Max file size: 105MB");
            return;
        }

        // Update files state and formik field value
        setFiles([file]);
        formikProps.setFieldValue("file", file);
        setError("");

        // Check if form is complete after file selection
        const updatedValues = { ...formikProps.values, file };
        if (checkFormCompletion(updatedValues)) {
            // Auto-upload with the selected file
            try {
                await uploadFiles(updatedValues, formikProps.resetForm, file);
            } catch (error) {
                console.error("Auto-upload error:", error);
                if (error?.response?.data?.error) {
                    setError(error.response.data.error);
                }
            }
        }
    };

    const onDrop = (acceptedFiles, fileRejections) => {
        if (fileRejections.length > 0) {
            const fileSizeError = fileRejections.some(
                rejection => rejection.errors.some(err => err.code === 'file-too-large')
            );
            if (fileSizeError) {
                setError("File size is too large. Please upload a smaller file. Max file size: 105MB");
            }
            return;
        }

        if (acceptedFiles.length > 0 && formikRef.current) {
            handleFileSelection(acceptedFiles[0], formikRef.current);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
            'application/pdf': ['.pdf'],
            'text/html': ['.html', '.htm']
        },
        maxSize: MAX_FILE_SIZE,
    });

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const modalTitle = marketingDocId ? "Upload new version" : "Add new document";
    const modalEyebrow = marketingDocId ? "Versioning" : "Marketing compliance";

    return (
        <AnimatePresence>
            {animationPlayed && (
                <motion.div
                    className={css.modalBackdrop}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="add-doc-modal-title"
                >
                    <motion.div
                        className={css.modalShell}
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.98 }}
                        transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
                    >
                        <div className={css.modalHeader}>
                            <div>
                                <p className={css.modalSubtitle}>{modalEyebrow}</p>
                                <h2 id="add-doc-modal-title" className={css.modalTitle}>{modalTitle}</h2>
                            </div>
                            <button
                                type="button"
                                className={css.modalClose}
                                onClick={closeModal}
                                aria-label="Close dialog"
                                disabled={loading}
                            >
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                        <div className={css.modalBody}>
                    <Formik
                        ref={formikRef}
                        initialValues={{
                            documentName: "",
                            file: null,
                            fields: {
                                documentType: [],
                                mediaChannel: [],
                                targetGeography: [],
                                prioritiesLevel: "",
                            },
                            description: "",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={async (values, { setSubmitting, resetForm }) => {
                            setSubmitting(true);
                            try {
                                await uploadFiles(values, resetForm);
                            } catch (error) {
                                if (error?.response?.data?.error) {
                                    setError(error.response.data.error);
                                }
                                setLoading(false);
                                setUploadProgress(0);
                                setUploadStage("");
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                    >
                        {(formikProps) => {
                            const { isSubmitting, errors, touched, setFieldValue, values } = formikProps;
                            setFieldValueRef.current = setFieldValue;
                            
                            // Store formik props in ref for dropzone access
                            formikRef.current = formikProps;

                            return (
                                <Form>
                                    {!marketingDocId && (
                                        <div className="m-2">
                                            <label htmlFor="documentName">Document Name <span className="text-danger">*</span></label>
                                            <Field
                                                className="form-control"
                                                type="text"
                                                name="documentName"
                                                placeholder="Enter your Document Name"
                                                id="documentName"
                                            />
                                            {errors.documentName && touched.documentName && (
                                                <div style={{ color: "red" }}>{errors.documentName}</div>
                                            )}
                                        </div>
                                    )}

                                    {!marketingDocId && (
                                        <div className="m-2">
                                            <SelectOption
                                                label={
                                                    <>
                                                        Document Type <span className="text-danger">*</span>
                                                    </>
                                                }
                                                name="fields.documentType"
                                                placeholder="Select Document Type"
                                                multi={true}
                                                options={
                                                    manageFields?.types
                                                        ? manageFields.types.map((item) => ({
                                                            value: item.type,
                                                            label: item.type,
                                                        }))
                                                        : []
                                                }
                                            />

                                            <SelectOption
                                                label={
                                                    <>
                                                        Media Channel <span className="text-danger">*</span>
                                                    </>
                                                }
                                                name="fields.mediaChannel"
                                                placeholder="Select Media Channel"
                                                multi={true}
                                                options={
                                                    manageFields?.channels
                                                        ? manageFields.channels.map((item) => ({
                                                            value: item.channel,
                                                            label: item.channel,
                                                        }))
                                                        : []
                                                }
                                            />

                                            <SelectOption
                                                label={
                                                    <>
                                                        Target Geography <span className="text-danger">*</span>
                                                    </>
                                                }
                                                name="fields.targetGeography"
                                                placeholder="Select Target Geography"
                                                multi={true}
                                                options={
                                                    manageFields?.geographies
                                                        ? manageFields.geographies.map((item) => ({
                                                            value: item.geography,
                                                            label: item.geography,
                                                        }))
                                                        : []
                                                }
                                            />

                                            <SelectOption
                                                label={
                                                    <>
                                                        Priority Level <span className="text-danger">*</span>
                                                    </>
                                                }
                                                name="fields.prioritiesLevel"
                                                placeholder="Select Priority Level"
                                                multi={false}
                                                options={
                                                    manageFields?.priorities
                                                        ? manageFields.priorities.map((item) => ({
                                                            value: item.priority,
                                                            label: item.priority,
                                                        }))
                                                        : []
                                                }
                                            />
                                        </div>
                                    )}

                                    {!marketingDocId && (
                                        <div className="m-2">
                                            <label htmlFor="description">Description</label>
                                            <Field
                                                className="form-control"
                                                as="textarea"
                                                name="description"
                                                placeholder="Enter your description"
                                            />
                                            {errors.description && touched.description && (
                                                <div style={{ color: "red" }}>{errors.description}</div>
                                            )}
                                        </div>
                                    )}

                                    {/* File upload section */}
                                    <label htmlFor="file" className="m-2 mb-0">Upload File <span className="text-danger">*</span></label>
                                    <p className="m-0 text-muted fw-light ms-2">
                                        Allowed file types: HTML, Images and PDFs (Max size: 105MB)
                                        {!marketingDocId && checkFormCompletion(values) && (
                                            <span className="text-success ms-2">✓ Ready for auto-upload</span>
                                        )}
                                        {!marketingDocId && !checkFormCompletion(values) && (
                                            <span className="text-warning ms-2">⚠ Complete all required fields first</span>
                                        )}
                                    </p>

                                    <div
                                        {...getRootProps()}
                                        className={`dropzone ${isDragActive ? "active" : ""} m-2`}
                                        style={{
                                            border: `2px dashed ${isDragActive ? "var(--accent)" : "var(--hairline-strong)"}`,
                                            padding: "20px",
                                            textAlign: "center",
                                            cursor: "pointer",
                                            backgroundColor: isDragActive ? "var(--accent-soft)" : "var(--surface)",
                                            borderRadius: "8px",
                                            transition: "all 0.3s ease",
                                            opacity: (!marketingDocId && !checkFormCompletion(values)) ? 0.6 : 1,
                                        }}
                                    >
                                        <input
                                            {...getInputProps()}
                                            onChange={(event) => {
                                                const file = event.target.files[0];
                                                if (file) {
                                                    handleFileSelection(file, formikProps);
                                                }
                                            }}
                                            disabled={!marketingDocId && !checkFormCompletion(values)}
                                        />
                                        {isDragActive ? (
                                            <p className="mb-0" style={{ color: "var(--accent)" }}>📁 Drop the file here...</p>
                                        ) : (
                                            <div>
                                                <p className="mb-2">📁 Drag and drop a file here, or click to select</p>
                                                {!marketingDocId && !checkFormCompletion(values) && (
                                                    <small className="text-warning">
                                                        <strong>Complete all required fields above to enable file upload</strong>
                                                    </small>
                                                )}
                                                {!marketingDocId && checkFormCompletion(values) && (
                                                    <small className="text-success">
                                                        <strong>Ready! File will upload automatically when selected</strong>
                                                    </small>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {errors.file && touched.file && (
                                        <div style={{ color: "red" }} className="ms-2">{errors.file}</div>
                                    )}

                                    {files.length > 0 && !loading && (
                                        <div className="m-2">
                                            <div className="alert alert-info">
                                                <strong>Selected file:</strong> {files[0].name} ({formatFileSize(files[0].size)})
                                            </div>
                                        </div>
                                    )}

                                    {/* Enhanced Progress bar with custom color */}
                                    {loading && (
                                        <div className="m-2">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span style={{ color: "var(--accent)", fontWeight: "600" }}>
                                                    {uploadStage || "Uploading..."}
                                                </span>
                                                <span style={{ color: "var(--accent)", fontWeight: "700" }}>
                                                    {uploadProgress}%
                                                </span>
                                            </div>
                                            <div 
                                                className="progress" 
                                                style={{ 
                                                    height: "12px", 
                                                    borderRadius: "6px",
                                                    backgroundColor: "var(--accent-soft)",
                                                    border: "1px solid #e5b4f3",
                                                    overflow: "hidden"
                                                }}
                                            >
                                                <div
                                                    className="progress-bar"
                                                    role="progressbar"
                                                    style={{ 
                                                        width: `${uploadProgress}%`,
                                                        backgroundColor: "var(--accent)",
                                                        transition: "width 0.5s ease",
                                                        borderRadius: "6px",
                                                        boxShadow: "0 2px 4px rgba(194, 29, 134, 0.3)",
                                                        background: "var(--accent)",
                                                        backgroundSize: "200% 100%",
                                                        animation: uploadProgress < 100 ? "shimmer 2s infinite" : "none"
                                                    }}
                                                    aria-valuenow={uploadProgress}
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                />
                                            </div>
                                            <div className="mt-2 text-center">
                                                <small style={{ color: "var(--ink-soft)", fontStyle: "italic" }}>
                                                    {uploadProgress === 100 ? 
                                                        "✅ " + uploadStage : 
                                                        "⏳ " + (uploadStage || "Processing your file...")
                                                    }
                                                </small>
                                            </div>
                                            
                                            {/* Add shimmer animation styles */}
                                            <style>
                                                {`
                                                    @keyframes shimmer {
                                                        0% {
                                                            background-position: -200% 0;
                                                        }
                                                        100% {
                                                            background-position: 200% 0;
                                                        }
                                                    }
                                                `}
                                            </style>
                                        </div>
                                    )}

                                    {error && (
                                        <div className="alert alert-danger m-2" role="alert">
                                            <strong>Error:</strong> {error}
                                            <button 
                                                type="button" 
                                                className="btn-close float-end" 
                                                aria-label="Close"
                                                onClick={() => setError("")}
                                            ></button>
                                        </div>
                                    )}
                                </Form>
                            );
                        }}
                    </Formik>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}